import React from "react";
import "./styles/PrivacyPolicy.css";

const PrivacyPolicy = () => {
  return (
    <div className="privacy-container">
      <div className="privacy-wrapper">
        <div className="privacy-header">
          <h1>Privacy Policy</h1>
          <p className="privacy-last-updated">
            Last Updated: February 12, 2026
          </p>
        </div>

        <div className="privacy-intro">
          <p>
            At ExploreEase, we take your privacy seriously. This Privacy Policy
            explains how we collect, use, disclose, and protect your information
            when you use our travel management platform. By using ExploreEase,
            you agree to the collection and use of information in accordance
            with this policy.
          </p>
        </div>

        <div className="privacy-toc">
          <h3>Table of Contents</h3>
          <ol>
            <li>
              <a href="#information-collection">Information We Collect</a>
            </li>
            <li>
              <a href="#how-we-use">How We Use Your Information</a>
            </li>
            <li>
              <a href="#data-sharing">Data Sharing and Disclosure</a>
            </li>
            <li>
              <a href="#data-storage">Data Storage and Security</a>
            </li>
            <li>
              <a href="#your-rights">Your Privacy Rights</a>
            </li>
            <li>
              <a href="#cookies">Cookies and Tracking Technologies</a>
            </li>
            <li>
              <a href="#third-party">Third-Party Services</a>
            </li>
            <li>
              <a href="#children">Children's Privacy</a>
            </li>
            <li>
              <a href="#international">International Data Transfers</a>
            </li>
            <li>
              <a href="#retention">Data Retention</a>
            </li>
            <li>
              <a href="#changes">Changes to This Policy</a>
            </li>
            <li>
              <a href="#contact">Contact Us</a>
            </li>
          </ol>
        </div>

        <div className="privacy-content">
          <section id="information-collection" className="privacy-section">
            <h2>1. Information We Collect</h2>

            <h3>1.1 Information You Provide Directly</h3>
            <p>
              When you use ExploreEase, we collect information that you
              voluntarily provide:
            </p>
            <ul>
              <li>
                <strong>Account Information:</strong> Name, email address,
                password, and profile details
              </li>
              <li>
                <strong>Trip Information:</strong> Destinations, dates,
                participant names, trip notes
              </li>
              <li>
                <strong>Travel Journal Content:</strong> Journal entries,
                photos, mood tracking data, ratings, and highlights
              </li>
              <li>
                <strong>Budget Data:</strong> Expenses, spending categories,
                participant splits, payment information
              </li>
              <li>
                <strong>Packing Lists:</strong> Items you add or modify in your
                packing lists
              </li>
              <li>
                <strong>Communication Data:</strong> Messages you send through
                our contact forms or support channels
              </li>
              <li>
                <strong>Payment Information:</strong> Billing details processed
                through our secure payment providers (we do not store complete
                credit card numbers)
              </li>
            </ul>

            <h3>1.2 Information Collected Automatically</h3>
            <p>
              We automatically collect certain information when you use our
              Service:
            </p>
            <ul>
              <li>
                <strong>Device Information:</strong> IP address, browser type,
                operating system, device identifiers
              </li>
              <li>
                <strong>Usage Data:</strong> Pages visited, features used, time
                spent on the platform, click patterns
              </li>
              <li>
                <strong>Location Data:</strong> General location based on IP
                address (we do not track precise GPS location)
              </li>
              <li>
                <strong>Cookies and Similar Technologies:</strong> See Section 6
                for details
              </li>
            </ul>

            <h3>1.3 Information from Third Parties</h3>
            <ul>
              <li>
                <strong>Authentication Providers:</strong> If you sign in via
                Google or other providers, we receive basic profile information
              </li>
              <li>
                <strong>Payment Processors:</strong> Transaction confirmation
                and billing information
              </li>
              <li>
                <strong>Analytics Services:</strong> Aggregated usage statistics
              </li>
            </ul>
          </section>

          <section id="how-we-use" className="privacy-section">
            <h2>2. How We Use Your Information</h2>
            <p>We use the collected information for the following purposes:</p>

            <h3>2.1 To Provide and Improve Our Service</h3>
            <ul>
              <li>Create and manage your account</li>
              <li>Enable trip organization and management features</li>
              <li>
                Generate AI-powered packing suggestions based on your trip
                details
              </li>
              <li>Provide personalized travel insights and recommendations</li>
              <li>Track budgets and calculate expense settlements</li>
              <li>Store and organize your travel journal and photos</li>
              <li>Improve our algorithms and enhance user experience</li>
            </ul>

            <h3>2.2 Communication</h3>
            <ul>
              <li>Send service-related notifications and updates</li>
              <li>Respond to your inquiries and support requests</li>
              <li>
                Send promotional emails about new features (you can opt out
                anytime)
              </li>
              <li>Request feedback or participation in surveys</li>
            </ul>

            <h3>2.3 Analytics and Research</h3>
            <ul>
              <li>Analyze usage patterns to improve our Service</li>
              <li>Monitor platform performance and troubleshoot issues</li>
              <li>Conduct internal research and development</li>
            </ul>

            <h3>2.4 Legal and Security</h3>
            <ul>
              <li>Protect against fraud, abuse, and security threats</li>
              <li>Comply with legal obligations and enforce our Terms</li>
              <li>
                Protect the rights and safety of ExploreEase and our users
              </li>
            </ul>
          </section>

          <section id="data-sharing" className="privacy-section">
            <h2>3. Data Sharing and Disclosure</h2>
            <p>
              We do not sell your personal information. We may share your
              information only in the following circumstances:
            </p>

            <h3>3.1 Service Providers</h3>
            <p>
              We share data with trusted third-party service providers who help
              us operate our platform:
            </p>
            <ul>
              <li>
                <strong>Firebase:</strong> Authentication and database services
              </li>
              <li>
                <strong>Payment Processors:</strong> Secure payment processing
                (Stripe, PayPal, etc.)
              </li>
              <li>
                <strong>OpenAI:</strong> AI-powered features (packing assistant,
                recommendations)
              </li>
              <li>
                <strong>Cloud Storage:</strong> Secure storage for photos and
                user content
              </li>
              <li>
                <strong>Email Services:</strong> Transactional and marketing
                emails
              </li>
              <li>
                <strong>Analytics Providers:</strong> Usage analytics and
                performance monitoring
              </li>
            </ul>
            <p className="privacy-note">
              These providers are contractually obligated to protect your data
              and use it only for the purposes we specify.
            </p>

            <h3>3.2 With Your Consent</h3>
            <p>
              We may share information when you explicitly consent, such as when
              you share trip details with other users or participants.
            </p>

            <h3>3.3 Legal Requirements</h3>
            <p>We may disclose your information if required by law, such as:</p>
            <ul>
              <li>
                In response to subpoenas, court orders, or legal processes
              </li>
              <li>
                To protect the rights, property, or safety of ExploreEase or
                others
              </li>
              <li>To investigate fraud or security issues</li>
              <li>To comply with regulatory requirements</li>
            </ul>

            <h3>3.4 Business Transfers</h3>
            <p>
              If ExploreEase is involved in a merger, acquisition, or sale of
              assets, your information may be transferred. We will notify you
              before your data is transferred and becomes subject to a different
              privacy policy.
            </p>
          </section>

          <section id="data-storage" className="privacy-section">
            <h2>4. Data Storage and Security</h2>

            <h3>4.1 Security Measures</h3>
            <p>
              We implement industry-standard security measures to protect your
              data:
            </p>
            <ul>
              <li>Encryption of data in transit (HTTPS/TLS)</li>
              <li>Encryption of sensitive data at rest</li>
              <li>Secure authentication and password hashing</li>
              <li>Regular security audits and updates</li>
              <li>Access controls and monitoring</li>
              <li>Secure cloud infrastructure (Firebase, AWS, etc.)</li>
            </ul>

            <h3>4.2 Data Storage Location</h3>
            <p>
              Your data is stored on secure servers provided by Firebase and
              other cloud service providers, primarily located in the United
              States. By using ExploreEase, you consent to the transfer and
              storage of your data in these locations.
            </p>

            <h3>4.3 No Absolute Security</h3>
            <p className="privacy-warning">
              While we strive to protect your personal information, no method of
              transmission over the Internet or electronic storage is 100%
              secure. We cannot guarantee absolute security of your data.
            </p>
          </section>

          <section id="your-rights" className="privacy-section">
            <h2>5. Your Privacy Rights</h2>
            <p>
              Depending on your location, you may have the following rights:
            </p>

            <h3>5.1 Access and Portability</h3>
            <ul>
              <li>Request a copy of your personal data</li>
              <li>Export your trip data, journal entries, and photos</li>
            </ul>

            <h3>5.2 Correction and Update</h3>
            <ul>
              <li>Update your account information at any time</li>
              <li>Correct inaccurate or incomplete data</li>
            </ul>

            <h3>5.3 Deletion</h3>
            <ul>
              <li>Delete your account and associated data</li>
              <li>Request deletion of specific information</li>
            </ul>

            <h3>5.4 Opt-Out Rights</h3>
            <ul>
              <li>Unsubscribe from marketing emails</li>
              <li>Disable certain cookies and tracking</li>
              <li>Opt out of data processing for certain purposes</li>
            </ul>

            <h3>5.5 GDPR Rights (EU Users)</h3>
            <p>
              If you are in the European Economic Area, you have additional
              rights:
            </p>
            <ul>
              <li>Right to data portability</li>
              <li>Right to restrict processing</li>
              <li>Right to object to processing</li>
              <li>Right to withdraw consent</li>
              <li>Right to lodge a complaint with a supervisory authority</li>
            </ul>

            <h3>5.6 CCPA Rights (California Users)</h3>
            <p>California residents have the right to:</p>
            <ul>
              <li>Know what personal information is collected</li>
              <li>Know whether personal information is sold or disclosed</li>
              <li>
                Opt out of the sale of personal information (we do not sell
                data)
              </li>
              <li>Request deletion of personal information</li>
              <li>Non-discrimination for exercising privacy rights</li>
            </ul>

            <h3>5.7 How to Exercise Your Rights</h3>
            <p>
              To exercise any of these rights, please contact us at{" "}
              <a href="mailto:info@explorease.com">info@explorease.com</a> or
              through your account settings. We will respond to your request
              within 30 days.
            </p>
          </section>

          <section id="cookies" className="privacy-section">
            <h2>6. Cookies and Tracking Technologies</h2>

            <h3>6.1 What Are Cookies?</h3>
            <p>
              Cookies are small text files stored on your device that help us
              provide and improve our Service. We use both session cookies
              (which expire when you close your browser) and persistent cookies
              (which remain until deleted).
            </p>

            <h3>6.2 Types of Cookies We Use</h3>
            <ul>
              <li>
                <strong>Essential Cookies:</strong> Required for basic
                functionality (login, security)
              </li>
              <li>
                <strong>Performance Cookies:</strong> Help us understand how you
                use the Service
              </li>
              <li>
                <strong>Functional Cookies:</strong> Remember your preferences
                and settings
              </li>
              <li>
                <strong>Analytics Cookies:</strong> Collect anonymous usage
                statistics
              </li>
            </ul>

            <h3>6.3 Managing Cookies</h3>
            <p>
              You can control cookies through your browser settings. Note that
              disabling certain cookies may limit your ability to use some
              features of ExploreEase.
            </p>
          </section>

          <section id="third-party" className="privacy-section">
            <h2>7. Third-Party Services</h2>
            <p>
              ExploreEase integrates with third-party services that have their
              own privacy policies. We are not responsible for the privacy
              practices of these external services:
            </p>
            <ul>
              <li>
                <strong>Firebase (Google):</strong>{" "}
                <a
                  href="https://firebase.google.com/support/privacy"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Privacy Policy
                </a>
              </li>
              <li>
                <strong>OpenAI:</strong>{" "}
                <a
                  href="https://openai.com/privacy"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Privacy Policy
                </a>
              </li>
              <li>
                <strong>Payment Processors:</strong> Stripe, PayPal (see their
                respective privacy policies)
              </li>
            </ul>
            <p>
              We encourage you to review the privacy policies of any third-party
              services you interact with through ExploreEase.
            </p>
          </section>

          <section id="children" className="privacy-section">
            <h2>8. Children's Privacy</h2>
            <p>
              ExploreEase is not intended for use by children under the age of
              13 (or 16 in the EU). We do not knowingly collect personal
              information from children. If we discover that we have collected
              information from a child without parental consent, we will delete
              that information immediately.
            </p>
            <p>
              If you believe we have collected information from a child, please
              contact us at{" "}
              <a href="mailto:info@explorease.com">info@explorease.com</a>.
            </p>
          </section>

          <section id="international" className="privacy-section">
            <h2>9. International Data Transfers</h2>
            <p>
              ExploreEase is based in the United States. If you access our
              Service from outside the US, your information may be transferred
              to, stored, and processed in the United States or other countries
              where our service providers operate.
            </p>
            <p>
              By using ExploreEase, you consent to the transfer of your
              information to countries that may have different data protection
              laws than your country of residence. We take steps to ensure your
              data receives adequate protection.
            </p>
          </section>

          <section id="retention" className="privacy-section">
            <h2>10. Data Retention</h2>
            <p>
              We retain your personal information for as long as necessary to:
            </p>
            <ul>
              <li>Provide you with our Service</li>
              <li>Comply with legal obligations</li>
              <li>Resolve disputes and enforce agreements</li>
              <li>Maintain security and prevent fraud</li>
            </ul>
            <p>
              When you delete your account, we will delete or anonymize your
              personal information within 90 days, except for data we are
              required to retain by law or for legitimate business purposes
              (e.g., fraud prevention, financial records).
            </p>
          </section>

          <section id="changes" className="privacy-section">
            <h2>11. Changes to This Privacy Policy</h2>
            <p>
              We may update this Privacy Policy from time to time to reflect
              changes in our practices or legal requirements. We will notify you
              of material changes by:
            </p>
            <ul>
              <li>Posting the updated policy on our website</li>
              <li>Sending an email notification to your registered address</li>
              <li>Displaying a prominent notice in the app</li>
            </ul>
            <p>
              Changes will take effect 30 days after notification. Your
              continued use of ExploreEase after the effective date constitutes
              acceptance of the updated policy.
            </p>
            <p className="privacy-note">
              The "Last Updated" date at the top of this page indicates when the
              policy was last revised. We encourage you to review this policy
              periodically.
            </p>
          </section>

          <section id="contact" className="privacy-section">
            <h2>12. Contact Us</h2>
            <p>
              If you have questions, concerns, or requests regarding this
              Privacy Policy or our data practices, please contact us:
            </p>
            <div className="contact-info-box">
              <p>
                General Support:{" "}
                <a href="mailto:info@explorease.com">info@explorease.com</a>
              </p>
              <p>Support Hours: Monday – Friday, 9:00 AM – 5:00 PM EST</p>
            </div>
            <p className="privacy-note">
              For GDPR-related inquiries, please include "GDPR Request" in your
              subject line. For CCPA requests, include "CCPA Request" in your
              subject line.
            </p>
          </section>
        </div>

        <div className="privacy-footer">
          <p>
            By using ExploreEase, you acknowledge that you have read and
            understood this Privacy Policy and agree to its terms.
          </p>
          <p className="privacy-copyright">
            © 2026 ExploreEase. All rights reserved.
          </p>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
